import { Link } from "@mui/material";
import { routes } from "@/global/routes";
import {
  faqs,
  howItWorks,
  RenoAcesTCListItems,
  RenoAcesCheckoutTerms,
  RenoAcesAdditionalDetailsDisclaimerTC,
} from "./helpers";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";
import { DefaultRegistrationTerms } from "../../default_terms_and_conditions";

import Background from "public/images/renoaces/background.jpg";
import Banner from "public/images/renoaces/reno-aces-banner.png";
import SeatMap3dPreview from "public/images/diamondbacks/seat_map_preview.png";

import type { TeamConfig } from "../types";

const Logo =
  "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/Reno-Aces_2.svg";

const config: TeamConfig = {
  teamSchema: "renoaces_suites",
  browserTitle: "Reno Aces Suites",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Reno Aces",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-31085-baseball-sec",
    mapId: "blockmap2d",
    hide3dViewFromSlider: true,
    isView3dButtonHidden: true,

    suiteImages: [
      "https://ss-leagues.s3.amazonaws.com/Minor League Baseball/Reno Aces Suites/040224_renoAces_v_sacramentoRiverCats_00197.jpg",
      "https://ss-leagues.s3.amazonaws.com/Minor League Baseball/Reno Aces Suites/040224_renoAces_v_sacramentoRiverCats_00197 (1).jpg",
      "https://ss-leagues.s3.amazonaws.com/Minor League Baseball/Reno Aces Suites/040224_renoAces_v_sacramentoRiverCats_00183.jpg",
      "https://ss-leagues.s3.amazonaws.com/Minor League Baseball/Reno Aces Suites/040224_renoAces_v_sacramentoRiverCats_00148.jpg",
      "https://ss-leagues.s3.amazonaws.com/Minor League Baseball/Reno Aces Suites/040224_renoAces_v_sacramentoRiverCats_00143.jpg",
      "https://ss-leagues.s3.amazonaws.com/Minor League Baseball/Reno Aces Suites/040224_renoAces_v_sacramentoRiverCats_00130.jpg",
    ],
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: inventoryTypeUIDs.suiteLevelV2,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    hideViewTicketsInThankYouPage: true,

    isCheckoutAdditionalDetailsEnabled: true,
    isPerksEnabled: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    googleTagManagerId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: false,

  // urls
  accountManagerUrl:
    "https://mlb.tickets.com/?agency=RENM_MYTIXX&orgid=53073#/auth/login?fullret=%2Fticketmanagement%2F%3Fagency%3DRENM_MYTIXX&orgid=53073",

  // vendor
  platform: vendorUUIDs.tickesdotcom,

  // okta config
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  oktaClientId: "0oasmcdgh92Jom7TI357",
  shouldUseInternalLogin: false,

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    faviconImage: Logo,
    authBackgroundImage: Background.src,
    findGamesBannerImage: Banner.src,
    checkoutStatusPageImage: Background.src,
    giftCardStandalonePageImage: Background.src,
    logoImage: Logo + "?width=128&height=66",
    seatMap3dPreviewImage: SeatMap3dPreview.src,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },
    bottomBar: {
      showGameSelectionProgress: false,
    },

    hidePackageBalanceOnCheckout: false,
    hideServiceFee: false,
    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    inventoryCardSettings: {
      isPBLineItemsVisible: true,
      seatCountLabel: "Tickets Included",
      pbLineItemsValue: 5,
      FBLabel: "Food & Beverage:",
      isParkingSeatCountVisible: false,
      FBValue: "Included",
    },
    inventoryContainer: {
      getGridCellHeight: (arg) => (arg?.mdDown ? 240 : 150),
    },
    checkoutPerkSubtotals: {
      ticketTotalLabel: "Total Ticket Balance",
      serviceFeeLabel: "Order Service Fee",
    },
    perks: {
      hideAddAllButton: true,
      displayOnlyPrice: ["foodUpgrade"],
      hideMandatoryPerkSeatCount: true,
      hideAddOnsViewer: true,
      hideCateringPerkItem: true,
    },
  },

  colors: {
    primary: "#002a5c",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: <RenoAcesTCListItems />,
    checkoutTermsAndCondition: <RenoAcesCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    additionalDetailsDisclaimerTC: <RenoAcesAdditionalDetailsDisclaimerTC />,
    purchaseSeats: {
      chooseAddOnsTabTitle: "Confirm Tickets",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription: (
      <>
        Your Reno Aces Suites confirmation was successful! If you have immediate
        questions, please reach out to{" "}
        <Link
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="tel:7753347079"
        >
          (775) 334-7079
        </Link>{" "}
        or email{" "}
        <Link
          href="mailto:jack.h@renoaces.com"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          jack.h@renoaces.com
        </Link>{" "}
        and a Reno Aces Representative will assist you. We will be in touch
        shortly to follow up as well.
      </>
    ),

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
